import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";
import { saveAs } from "file-saver";
import { isArray } from "lodash";
import numeral from "numeral";
import { prepareFormObjectForSave } from "@/core/tools/forms/prepare_form_object_for_save";
import { DocumentId } from "@/core/uql/classes/document_id";
import {
  buildMonthPlanDistribution,
  monthPlanKgFromContainers,
  splitProportionalInt,
  weekBoundsInReportMonth
} from "./monthPlanDistribution";
import { Plugins } from "@capacitor/core";
const { Storage } = Plugins;

interface ShipmentForecastState {
  loaded: boolean;
  full_reloading: boolean;
  loading: boolean;
  fatalError: boolean;

  filter: {
    dZoneGroups: any;
    targetDates: Date;
  };

  schema: any;
  items: any[];
  data: any[];
  weeks: any[];
  settings: any;
  targetUpdate: any[];
  expandedRows: any;
  splitFactPrevMonths: boolean;
  prevFactWeekColumns: any[];
  /** Последние параметры экрана (без служебных полей) для перезагрузки после экспорта */
  lastQueryParams: any;
  /** Серверная пагинация (клиенты в отчёте) */
  reportTablePage: number;
  reportTablePerPage: number;
  reportTotalCount: number;
}

const state: ShipmentForecastState = {
  loading: false,
  loaded: false,
  full_reloading: false,
  fatalError: false,

  filter: {
    dZoneGroups: null,
    targetDates: new Date()
  },

  schema: null,
  items: [],
  data: [],
  weeks: [],
  settings: {},
  targetUpdate: [],
  expandedRows: {},
  splitFactPrevMonths: false,
  prevFactWeekColumns: [],
  lastQueryParams: {},
  reportTablePage: 1,
  reportTablePerPage: 20,
  reportTotalCount: 0
};

/** Макс. клиентов при экспорте (один запрос к shipment_forecast). */
const SHIPMENT_FORECAST_EXPORT_MAX_CLIENTS = 50000;

/** Синхронно с `Cheese.Batch.ShipmentForecast.@shipment_forecast_plan_sql_cutover` */
const SHIPMENT_FORECAST_PLAN_CUTOVER = "2026-05-01";

function shipmentForecastUsesSegmentWeekKeys(targetDates: any) {
  if (!targetDates) return false;
  return moment(targetDates).isBefore(SHIPMENT_FORECAST_PLAN_CUTOVER, "day");
}

/** Дни [weekStart..weekEnd], попадающие в календарный месяц targetDates. */
function daysOfWeekInReportMonth(
  weekStart: string,
  weekEnd: string,
  targetDates: any
): number {
  if (!targetDates) return 7;
  const ms = moment(targetDates).startOf("month");
  const me = moment(targetDates).endOf("month");
  const ws = moment(weekStart).startOf("day");
  const we = moment(weekEnd).startOf("day");
  const s = ws.isAfter(ms) ? ws : ms;
  const e = we.isBefore(me) ? we : me;
  if (e.isBefore(s)) return 0;
  return e.diff(s, "days") + 1;
}

/** Длины сегментов по границе месяца внутри одной ISO-недели (1 или 2 сегмента). */
function isoWeekSegmentDayCounts(weekStart: string, weekEnd: string): number[] {
  const ws = moment(weekStart).startOf("day");
  const we = moment(weekEnd).startOf("day");
  if (ws.month() === we.month() && ws.year() === we.year()) {
    return [we.diff(ws, "days") + 1];
  }
  const endM = ws.clone().endOf("month");
  const d1 = endM.diff(ws, "days") + 1;
  const d2 = we.diff(endM.clone().add(1, "day"), "days") + 1;
  return [d1, d2];
}

function segmentBoundsForIsoWeek(
  weekStart: string,
  weekEnd: string
): { start: string; end: string }[] {
  const ws = moment(weekStart).startOf("day");
  const we = moment(weekEnd).startOf("day");
  if (ws.month() === we.month() && ws.year() === we.year()) {
    return [{ start: ws.format("YYYY-MM-DD"), end: we.format("YYYY-MM-DD") }];
  }
  const endM = ws.clone().endOf("month");
  return [
    { start: ws.format("YYYY-MM-DD"), end: endM.format("YYYY-MM-DD") },
    {
      start: endM.clone().add(1, "day").format("YYYY-MM-DD"),
      end: we.format("YYYY-MM-DD")
    }
  ];
}

function buildFilter(params: any) {
  const queryParams: any = {};

  if (params.targetDates) {
    queryParams.date_from = moment(params.targetDates)
      .startOf("month")
      .format("YYYY-MM-DD");
    queryParams.date_to = moment(params.targetDates)
      .startOf("month")
      .add(1, "month")
      .subtract(1, "day")
      .format("YYYY-MM-DD");
  }

  if (params.dZoneGroups) {
    queryParams.delivery_zone_id = params.dZoneGroups;
  }

  if (params.sortField !== null) {
    queryParams.order_by = params.sortField;
    queryParams.order_type = params.sortDirection;
  }

  return queryParams;
}

function getProductByPath(
  state: ShipmentForecastState,
  row_id: number,
  address_id: number,
  product_id: number,
  point_id?: number | null
) {
  const address = state.items[row_id].addresses[address_id];
  if (point_id != null && address.points && address.points[point_id]) {
    return address.points[point_id].products[product_id];
  }
  return address.products[product_id];
}

const DELIVERY_POINT_DOC_PREFIX = "d:delivery_points:";

/**
 * DocAPI `documents.create` ждёт relation id строкой (`d:delivery_points:…`), не число.
 * В отчёте id может быть уже строкой или числом/PG integer из `address_ids`.
 */
function deliveryPointDocIdForPayload(raw: any): string | null {
  if (raw === null || raw === undefined || raw === "") return null;
  if (typeof raw === "string") {
    const s = raw.trim();
    if (s.startsWith("d:")) {
      return DocumentId.valid(s) ? s : null;
    }
    const pk = parseInt(s, 10);
    return Number.isFinite(pk) ? `${DELIVERY_POINT_DOC_PREFIX}${pk}` : null;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return `${DELIVERY_POINT_DOC_PREFIX}${Math.trunc(raw)}`;
  }
  return null;
}

/** Когда в отчёте ещё нет строк week_plan, бэкенд отдаёт пустые plan_ids / address_ids — берём id точки из дерева таблицы. */
function deliveryPointIdFromAddressRow(
  state: ShipmentForecastState,
  row_id: number,
  address_id: number,
  point_id?: number | null
): string | null {
  const client = state.items[row_id];
  if (!client || !client.addresses[address_id]) return null;
  const address = client.addresses[address_id];
  if (point_id != null && address.points && address.points[point_id]) {
    return deliveryPointDocIdForPayload(address.points[point_id].id);
  }
  return deliveryPointDocIdForPayload(address.id);
}

function weekPlanSegmentDeliveryPointId(
  state: ShipmentForecastState,
  row_id: number,
  address_id: number,
  point_id: number | null | undefined,
  wk: any,
  segmentIdx: number
): string | null {
  const addrIds = wk.address_ids;
  if (isArray(addrIds) && addrIds[segmentIdx] != null && addrIds[segmentIdx] !== "") {
    return deliveryPointDocIdForPayload(addrIds[segmentIdx]);
  }
  if (isArray(addrIds) && addrIds.length > 0 && addrIds[0] != null && addrIds[0] !== "") {
    return deliveryPointDocIdForPayload(addrIds[0]);
  }
  return deliveryPointIdFromAddressRow(state, row_id, address_id, point_id);
}

function weekPlanSaveSegmentIndices(planIds: any[]): number[] {
  return planIds.length > 0 ? planIds.map((_, i) => i) : [0];
}

function resolveReportMonthPlanSegmentIndex(
  weekStart: string,
  weekEnd: string,
  targetDates: any
): number {
  const reportBounds = weekBoundsInReportMonth(weekStart, weekEnd, targetDates);
  const allBounds = segmentBoundsForIsoWeek(weekStart, weekEnd);
  if (allBounds.length <= 1) {
    return 0;
  }
  const idx = allBounds.findIndex(
    b => b.start === reportBounds.start && b.end === reportBounds.end
  );
  return idx >= 0 ? idx : allBounds.length - 1;
}

function buildWeekPlanPersistSegments(
  state: ShipmentForecastState,
  wk: any,
  ids: any[],
  containerCount: number,
  containerWeightKg: number
) {
  const targetDates = state.lastQueryParams && state.lastQueryParams.targetDates;
  const segs = isoWeekSegmentDayCounts(wk.week_start, wk.week_end);
  const useSplit =
    ids.length > 1 && !shipmentForecastUsesSegmentWeekKeys(targetDates);
  const normalizedWeight = monthPlanKgFromContainers(
    containerCount,
    containerWeightKg
  );

  if (useSplit) {
    const wParts = segs.length === ids.length ? segs : ids.map(() => 1);
    const bounds = segmentBoundsForIsoWeek(wk.week_start, wk.week_end);
    const subPlans = splitProportionalInt(containerCount, wParts);
    const subKgCents = splitProportionalInt(
      Math.round(normalizedWeight * 100),
      wParts
    );
    const subKg = subKgCents.map(x => x / 100);

    return weekPlanSaveSegmentIndices(ids).map(idx => {
      const seg = bounds[idx] || bounds[0];
      return {
        segmentIdx: idx,
        plan_id: ids[idx],
        week_start: seg.start,
        week_end: seg.end,
        plan: subPlans[idx] != null ? subPlans[idx] : containerCount,
        plan_weight: subKg[idx] != null ? subKg[idx] : normalizedWeight
      };
    });
  }

  const bounds = weekBoundsInReportMonth(
    wk.week_start,
    wk.week_end,
    targetDates
  );
  const segmentIdx = resolveReportMonthPlanSegmentIndex(
    wk.week_start,
    wk.week_end,
    targetDates
  );
  const planId =
    ids.length > 0 ? ids[segmentIdx] || ids[ids.length - 1] : undefined;

  return [
    {
      segmentIdx,
      plan_id: planId,
      week_start: bounds.start,
      week_end: bounds.end,
      plan: containerCount,
      plan_weight: normalizedWeight
    }
  ];
}

function pushWeekPlanTargetUpdates(
  state: ShipmentForecastState,
  {
    row_id,
    address_id,
    product_id,
    point_id,
    week_key,
    wk,
    segments
  }: {
    row_id: number;
    address_id: number;
    product_id: number;
    point_id: number | null | undefined;
    week_key: string;
    wk: any;
    segments: Array<{
      segmentIdx: number;
      plan_id: string | undefined;
      week_start: string;
      week_end: string;
      plan: number;
      plan_weight: number;
    }>;
  }
) {
  segments.forEach(segment => {
    const plan_id = segment.plan_id;
    const key = `${row_id}-${address_id}-${point_id == null ? "root" : point_id}-${product_id}-${week_key}-${segment.segmentIdx}`;
    const _exists: any = state.targetUpdate.filter(
      (plan: any) => plan.id === plan_id || plan.id === key
    );
    const row = {
      id: plan_id ? plan_id : key,
      year: wk.year,
      week_num: wk.week_num,
      week_start: segment.week_start,
      week_end: segment.week_end,
      delivery_point_id: weekPlanSegmentDeliveryPointId(
        state,
        row_id,
        address_id,
        point_id,
        wk,
        segment.segmentIdx
      ),
      product_container_id: wk.product_container_id,
      coef: "1.0",
      avg_quantity: 0,
      plan: segment.plan
    };
    if (!_exists.length) {
      state.targetUpdate.push(row);
    } else {
      Object.assign(_exists[0], row);
    }
  });
}

const LOG_FIX =
  process.env.LOG_LEVEL === "debug" || process.env.DEBUG_FIX === "1";

function applyMonthPlanDistribution(
  state: ShipmentForecastState,
  {
    row_id,
    address_id,
    product_id,
    point_id,
    monthPlanKg
  }: {
    row_id: number;
    address_id: number;
    product_id: number;
    point_id: number | null | undefined;
    monthPlanKg: number;
  }
) {
  const product = getProductByPath(
    state,
    row_id,
    address_id,
    product_id,
    point_id
  );
  const td = state.lastQueryParams && state.lastQueryParams.targetDates;
  const sortedKeys = Object.keys(product.weeks).sort((a, b) =>
    String(product.weeks[a].week_start).localeCompare(
      String(product.weeks[b].week_start)
    )
  );
  const weeksInMonth = sortedKeys.map(weekKey => ({
    weekKey,
    daysInMonth: daysOfWeekInReportMonth(
      product.weeks[weekKey].week_start,
      product.weeks[weekKey].week_end,
      td
    )
  }));
  const distribution = buildMonthPlanDistribution(
    monthPlanKg,
    product.product_weight,
    weeksInMonth
  );

  if (LOG_FIX) {
    // eslint-disable-next-line no-console
    console.log("[FIX] month plan distribution", {
      inputKg: monthPlanKg,
      containerWeight: product.product_weight,
      monthPlanKg: distribution.monthPlanKg,
      totalContainers: distribution.totalContainers,
      weekCount: sortedKeys.length,
      weekContainers: distribution.weekContainers
    });
  }

  product.month_plan = distribution.monthPlanKg;
  product.delta = product.month_plan - product.month_quantity;

  sortedKeys.forEach(week_key => {
    const part = distribution.weekContainers[week_key] || 0;
    const wk = product.weeks[week_key];
    wk.plan = part;
    wk.plan_weight = monthPlanKgFromContainers(part, product.product_weight);
    wk.plan_manual_weight =
      Math.round(wk.plan_manual * product.product_weight * 100) / 100;

    const ids = wk.plan_ids || [];
    const segments = buildWeekPlanPersistSegments(
      state,
      wk,
      ids,
      part,
      product.product_weight
    );
    pushWeekPlanTargetUpdates(state, {
      row_id,
      address_id,
      product_id,
      point_id,
      week_key,
      wk,
      segments
    });
  });
}

function applyWeekPlanNormalization(
  state: ShipmentForecastState,
  {
    row_id,
    address_id,
    product_id,
    point_id,
    week_key
  }: {
    row_id: number;
    address_id: number;
    product_id: number;
    point_id: number | null | undefined;
    week_key: string;
  }
) {
  const product = getProductByPath(
    state,
    row_id,
    address_id,
    product_id,
    point_id
  );
  const plan = product.weeks[week_key];
  const plan_weight = parseFloat(plan.plan_weight) || 0;
  const containerWeight = parseFloat(plan.product_weight) || product.product_weight;
  const new_plan = Math.ceil(
    containerWeight > 0 ? plan_weight / containerWeight : plan_weight
  );
  const normalizedWeight = monthPlanKgFromContainers(new_plan, containerWeight);
  const ids = plan.plan_ids || [];
  const segments = buildWeekPlanPersistSegments(
    state,
    plan,
    ids,
    new_plan,
    containerWeight
  );
  pushWeekPlanTargetUpdates(state, {
    row_id,
    address_id,
    product_id,
    point_id,
    week_key,
    wk: plan,
    segments
  });
  plan.plan = new_plan;
  plan.plan_weight = normalizedWeight;
  product.month_plan =
    Math.round(
      Object.keys(product.weeks).reduce((sum, key) => {
        return sum + (parseFloat(product.weeks[key].plan_weight) || 0);
      }, 0) * 100
    ) / 100;
  product.delta = product.month_plan - product.month_quantity;
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[ShipmentForecast] week plan normalized", {
      week_key,
      plan_weight: normalizedWeight,
      containers: new_plan
    });
  }
}

export const REPORT_DATA = "GET_REPORT_DATA";
export const SCHEMA_DATA = "GET_SCHEMA_DATA";
export const SETTING_VALUES_DATA = "SETTING_VALUES_DATA";

export const actions: ActionTree<ShipmentForecastState, RootState> = {
  reloadData({ commit, state, dispatch }, params) {
    params.reload = true;
    dispatch("loadData", params);
  },
  setReportTablePage({ commit }, page: number) {
    commit("SET_REPORT_TABLE_PAGE", page);
  },
  loadData({ commit, state, dispatch }, params) {
    const vm = (this as any)._vm;

    const persistParams = { ...params };
    delete persistParams._exportFull;
    delete persistParams.reload;
    delete persistParams.showColumns;
    delete persistParams.no_products;
    commit("SET_LAST_QUERY_PARAMS", persistParams);

    const exportFull = params._exportFull === true;

    const queryParams: any = {};

    if (params.targetDates) {
      queryParams.date_from = moment(params.targetDates)
        .startOf("month")
        .format("YYYY-MM-DD");
      queryParams.date_to = moment(params.targetDates)
        .startOf("month")
        .add(1, "month")
        .subtract(1, "day")
        .format("YYYY-MM-DD");
    }
    if (params.employee_id && params.employee_id.length > 0) {
      queryParams.employee_id = Array.isArray(params.employee_id)
        ? params.employee_id
        : [params.employee_id];
    }
    if (params.client_name) {
      queryParams.client_name = params.client_name;
    }
    if (params.client_id) {
      queryParams.client_id = params.client_id;
    }
    if (params.delivery_zone_group_id) {
      queryParams.delivery_zone_group_id = params.delivery_zone_group_id;
    }
    if (params.product_types && params.product_types.length > 0) {
      queryParams.product_types = Array.isArray(params.product_types)
        ? params.product_types
        : [params.product_types];
    }
    if (
      params.with_archived === "1" ||
      params.with_archived === 1 ||
      params.with_archived === true
    ) {
      queryParams.with_archived = "1";
    }
    if (params.only_custom_plan) {
      queryParams.only_custom_plan = params.only_custom_plan;
    }
    if (params.category_income) {
      queryParams.category_income = params.category_income;
    }

    const settingNames = [
      "forecast_low",
      "forecast_high",
      "shipment_forecast_avg_weeks"
    ];

    const applySettingsToQuery = (settingsMap: Record<string, string>) => {
      const avgW = parseInt(settingsMap.shipment_forecast_avg_weeks || "4", 10);
      const x = Number.isFinite(avgW) ? Math.min(Math.max(avgW, 1), 53) : 4;
      queryParams.avg_weeks = String(x);
      if (
        params.only_custom_plan === "1" &&
        params.split_fact_prev_months === "1"
      ) {
        queryParams.split_fact_prev_months = "1";
      }
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.debug("[ShipmentForecast] queryParams weeks", {
          date_from: queryParams.date_from,
          date_to: queryParams.date_to,
          avg_weeks: queryParams.avg_weeks,
          split_fact_prev_months: queryParams.split_fact_prev_months
        });
      }
    };

    return new Promise((resolve, reject) => {
      if (Object.values(params).length > 0) {
        commit(params.reload ? "START_RELOADING" : "START_LOADING");

        vm
          .$db([
            vm
              .$q(SETTING_VALUES_DATA)
              .raw("documents.list")
              .action({
                columns: {
                  name: "s.name",
                  value: "s.value"
                },
                from: "s:settings",
                from_as: "s",
                where: {
                  $in: ["s.name", { $: settingNames }]
                },
                limit: 20
              })
          ])
          .then((settingsResult: ServerResult) => {
            const docs = settingsResult
              .getOperation(SETTING_VALUES_DATA)
              .getContent().documents;
            const settingsMap = docs.reduce(
              (acc: Record<string, string>, item: any) => {
                acc[item.name] = item.value;
                return acc;
              },
              {}
            );
            applySettingsToQuery(settingsMap);

            // Frontend pagination: always load full filtered dataset.
            queryParams.limit = String(SHIPMENT_FORECAST_EXPORT_MAX_CLIENTS);
            queryParams.offset = "0";

            return vm
              .$db([
                vm
                  .$q(REPORT_DATA)
                  .raw("shipment_forecast")
                  .action(queryParams),
                vm
                  .$q(SCHEMA_DATA)
                  .raw("schemas.get")
                  .action({
                    id: "s:client_product_containers_week_plan"
                  })
              ])
              .then((result: ServerResult) => {
                const reportContent = result.getOperation(REPORT_DATA).getContent();
                commit("SET_REPORT_DATA", {
                  documents: reportContent.documents,
                  meta: reportContent.meta,
                  schema: result.getOperation(SCHEMA_DATA).getContent().schema,
                  settings: docs,
                  splitFactPrevMonths:
                    params.only_custom_plan === "1" &&
                    params.split_fact_prev_months === "1"
                });
                commit("SET_ITEMS", null);
                commit("LOADED_SUCCESS");

                resolve(null);
              });
          })
          .catch((err: any) => {
            commit("LOADED_ERROR");
            reject(err);
          });
      } else {
        resolve(null);
      }
    });
  },

  clear({ commit }) {
    commit("CLEAR");
  },

  setPlanValue(
    { commit },
    { row_id, address_id, product_id, point_id, week_key, value }
  ) {
    commit("ADD_UPDATE_PLAN_VALUE", {
      row_id,
      address_id,
      point_id,
      product_id,
      week_key,
      value
    });
  },

  setMonthPlanValue(
    { commit },
    { row_id, address_id, product_id, point_id, value }
  ) {
    commit("ADD_UPDATE_MONTH_PLAN_VALUE", {
      row_id,
      address_id,
      point_id,
      product_id,
      value
    });
  },

  recalcMonthPlan({ commit }, { row_id, address_id, product_id, point_id }) {
    commit("RECALC_MONTH_PLAN_VALUE", {
      row_id,
      address_id,
      product_id,
      point_id
    });
  },

  recalcWeekPlan(
    { commit },
    { row_id, address_id, product_id, point_id, week_key }
  ) {
    commit("RECALC_WEEK_PLAN_VALUE", {
      row_id,
      address_id,
      product_id,
      point_id,
      week_key
    });
  },

  savePlans({ state, commit }) {
    const vm = (this as any)._vm;
    commit("START_RELOADING");

    const rows = state.targetUpdate.map((plan: any, idx: any) => {
      const plan_id = plan.id;
      plan.plan = !plan.plan
        ? "0.0"
        : numeral(plan.plan)
            .value()
            .toFixed(2);
      plan = prepareFormObjectForSave(state.schema, plan);
      delete plan.id;
      return plan_id &&
        plan_id.startsWith("d:client_product_containers_week_plan")
        ? vm.$q("DATA_SAVE-" + idx).update(plan_id, { plan: plan.plan })
        : vm
            .$q("DATA_SAVE-" + idx)
            .create("s:client_product_containers_week_plan", plan);
    });

    return new Promise((resolve, reject) => {
      vm.$db(rows).then((result: ServerResult) => {
        commit("CLEAR_TARGET_UPDATE");
        commit("LOADED_SUCCESS");
        resolve(null);
      });
    });
  },

  fixPlans({ state, commit, dispatch }) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit("START_RELOADING");

      vm.$http.get(`/api/v1/fix_plan`, {}, {}).then((response: any) => {
        resolve(null);
      });
    });
  },

  exportData({ state, getters, dispatch, commit }, params) {
    const savedTablePage = state.reportTablePage;
    const date = moment(params.targetDates);
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[ShipmentForecast] export", {
        report_month: date.format("YYYY-MM"),
        week_columns: state.weeks.length
      });
    }
    return new Promise((resolve, reject) => {
      dispatch("loadData", { ...params, _exportFull: true })
        .then(() => {
      const csvContent: any[] = [];
      const header = [
        ...(params.showColumns.includes("category_income") ? ["Кат."] : []),
        ...(params.showColumns.includes("client_name") ? ["Клиент"] : []),
        ...(params.showColumns.includes("manager_name") ? ["Менеджер"] : []),
        ...(params.showColumns.includes("secondary_manager_name")
          ? ["Со-менеджер"]
          : []),
        ...(params.showColumns.includes("fact_d3")
          ? [
              `Факт ${moment(date)
                .subtract(3, "months")
                .format("MM.YYYY")}`
            ]
          : []),
        ...(state.splitFactPrevMonths &&
        (params.showColumns.includes("fact_d2") ||
          params.showColumns.includes("fact_d1"))
          ? state.prevFactWeekColumns.map(
              (col: any) =>
                `Нед ${col.iso_week} ${moment(col.week_start).format(
                  "DD.MM"
                )}-${moment(col.week_end).format("DD.MM")}`
            )
          : []),
        ...(!state.splitFactPrevMonths && params.showColumns.includes("fact_d2")
          ? [
              `Факт ${moment(date)
                .subtract(2, "months")
                .format("MM.YYYY")}`
            ]
          : []),
        ...(!state.splitFactPrevMonths && params.showColumns.includes("fact_d1")
          ? [
              `Факт ${moment(date)
                .subtract(1, "month")
                .format("MM.YYYY")}`
            ]
          : []),
        ...(params.showColumns.includes("fact_avg") ? ["Среднее"] : []),
        ...(params.showColumns.includes("fact_py")
          ? [
              `Факт ${moment(date)
                .subtract(1, "year")
                .format("MM.YYYY")}`
            ]
          : []),
        ...(params.showColumns.includes("plan")
          ? [`План ПР ${moment(date).format("MM.YYYY")}`]
          : []),
        ...(params.showColumns.includes("plan_manual")
          ? [`План Ц ${moment(date).format("MM.YYYY")}`]
          : []),
        ...(params.showColumns.includes("fact")
          ? [`Факт ${moment(date).format("MM.YYYY")}`]
          : []),
        ...(params.showColumns.includes("delta")
          ? [`Дельта ${moment(date).format("MM.YYYY")}`]
          : []),
        ...(params.showColumns.includes("percent") ? [`Процент`] : [])
      ];
      const subheader = [
        ...(params.showColumns.includes("client_name") ? [""] : []),
        ...(params.showColumns.includes("manager_name") ? [""] : []),
        ...(params.showColumns.includes("secondary_manager_name") ? [""] : []),
        ...(params.showColumns.includes("fact_d3") ? [""] : []),
        ...(state.splitFactPrevMonths &&
        (params.showColumns.includes("fact_d2") ||
          params.showColumns.includes("fact_d1"))
          ? state.prevFactWeekColumns.map(() => "")
          : []),
        ...(!state.splitFactPrevMonths && params.showColumns.includes("fact_d2")
          ? [""]
          : []),
        ...(!state.splitFactPrevMonths && params.showColumns.includes("fact_d1")
          ? [""]
          : []),
        ...(params.showColumns.includes("fact_avg") ? [""] : []),
        ...(params.showColumns.includes("fact_py") ? [""] : []),
        ...(params.showColumns.includes("plan") ? [""] : []),
        ...(params.showColumns.includes("plan_manual") ? [""] : []),
        ...(params.showColumns.includes("fact") ? [""] : []),
        ...(params.showColumns.includes("delta") ? [""] : []),
        ...(params.showColumns.includes("percent") ? [""] : [])
      ];
      if (
        params.showColumns.includes("week_plan") ||
        params.showColumns.includes("week_plan_manual") ||
        params.showColumns.includes("week_fact")
      ) {
        const monthLabel = moment(date).format("MM.YYYY");
        state.weeks.forEach((week: any) => {
          const wn = week.week_num != null ? `нед.${week.week_num} ` : "";
          header.push(
            `${wn}${moment(week.week_start).format("DD.MM")}–${moment(
              week.week_end
            ).format("DD.MM")} (доля ${monthLabel})`
          );
          params.showColumns.includes("week_plan_manual") &&
          params.showColumns.includes("week_plan")
            ? header.push("")
            : 0;
          params.showColumns.includes("week_fact") ? header.push("") : 0;
          params.showColumns.includes("week_plan")
            ? subheader.push("План ПР")
            : 0;
          params.showColumns.includes("week_plan_manual")
            ? subheader.push("План Ц")
            : 0;
          params.showColumns.includes("week_fact") ? subheader.push("Факт") : 0;
        });
      }

      csvContent.push(header);
      csvContent.push(subheader);
      const sumheader = [
        ...(params.showColumns.includes("category_income") ? [""] : []),
        ...(params.showColumns.includes("client_name") ? ["ИТОГО"] : []),
        ...(params.showColumns.includes("manager_name")
          ? [getters.total.prev_3_quantity]
          : []),
        ...(params.showColumns.includes("secondary_manager_name")
          ? [getters.total.prev_3_quantity]
          : []),
        ...(params.showColumns.includes("fact_d3")
          ? [getters.total.prev_3_quantity]
          : []),
        ...(state.splitFactPrevMonths &&
        (params.showColumns.includes("fact_d2") ||
          params.showColumns.includes("fact_d1"))
          ? state.prevFactWeekColumns.map(
              (col: any) =>
                getters.total.prevFactByKey[col.key] != null
                  ? getters.total.prevFactByKey[col.key]
                  : 0
            )
          : []),
        ...(!state.splitFactPrevMonths && params.showColumns.includes("fact_d2")
          ? [getters.total.prev_2_quantity]
          : []),
        ...(!state.splitFactPrevMonths && params.showColumns.includes("fact_d1")
          ? [getters.total.prev_1_quantity]
          : []),
        ...(params.showColumns.includes("fact_avg")
          ? [getters.total.prev_avg_quantity]
          : []),
        ...(params.showColumns.includes("fact_py")
          ? [getters.total.prev_year_quantity]
          : []),
        ...(params.showColumns.includes("plan")
          ? [getters.total.month_plan]
          : []),
        ...(params.showColumns.includes("plan_manual")
          ? [getters.total.month_plan_manual]
          : []),
        ...(params.showColumns.includes("fact")
          ? [getters.total.month_quantity]
          : []),
        ...(params.showColumns.includes("delta") ? [getters.total.delta] : []),
        ...(params.showColumns.includes("percent")
          ? [getters.total.percent]
          : [])
      ];
      if (
        params.showColumns.includes("week_plan") ||
        params.showColumns.includes("week_plan_manual") ||
        params.showColumns.includes("week_fact")
      ) {
        state.weeks.forEach((week: any) => {
          params.showColumns.includes("week_plan")
            ? sumheader.push(getters.total.weeks[week.key].plan)
            : 0;
          params.showColumns.includes("week_plan_manual")
            ? sumheader.push(getters.total.weeks[week.key].plan_manual)
            : 0;
          params.showColumns.includes("week_fact")
            ? sumheader.push(getters.total.weeks[week.key].fact)
            : 0;
        });
      }
      csvContent.push(sumheader);
      state.items.forEach((item: any) => {
        const csv_row = [
          ...(params.showColumns.includes("category_income")
            ? [`[${item.category_income}]`]
            : []),
          ...(params.showColumns.includes("client_name") ? [item.name] : []),
          ...(params.showColumns.includes("manager_name")
            ? [item.manager_name]
            : []),
          ...(params.showColumns.includes("secondary_manager_name")
            ? [item.secondary_manager_name]
            : []),
          ...(params.showColumns.includes("fact_d3")
            ? [parseFloat(item.prev_3_quantity) || 0]
            : []),
          ...(state.splitFactPrevMonths &&
          (params.showColumns.includes("fact_d2") ||
            params.showColumns.includes("fact_d1"))
            ? state.prevFactWeekColumns.map(
                (col: any) =>
                  (item.prevFactByKey && item.prevFactByKey[col.key]) || 0
              )
            : []),
          ...(!state.splitFactPrevMonths &&
          params.showColumns.includes("fact_d2")
            ? [parseFloat(item.prev_2_quantity) || 0]
            : []),
          ...(!state.splitFactPrevMonths &&
          params.showColumns.includes("fact_d1")
            ? [parseFloat(item.prev_1_quantity) || 0]
            : []),
          ...(params.showColumns.includes("fact_avg")
            ? [parseFloat(item.prev_avg_quantity) || 0]
            : []),
          ...(params.showColumns.includes("fact_py")
            ? [parseFloat(item.prev_year_quantity) || 0]
            : []),
          ...(params.showColumns.includes("plan")
            ? [parseFloat(item.month_plan) || 0]
            : []),
          ...(params.showColumns.includes("plan_manual")
            ? [parseFloat(item.month_plan_manual) || 0]
            : []),
          ...(params.showColumns.includes("fact")
            ? [parseFloat(item.month_quantity) || 0]
            : []),
          ...(params.showColumns.includes("delta")
            ? [parseFloat(item.delta) || 0]
            : []),
          ...(params.showColumns.includes("percent")
            ? [parseFloat(item.percent) || 0]
            : [])
        ];

        if (
          params.showColumns.includes("week_plan") ||
          params.showColumns.includes("week_plan_manual") ||
          params.showColumns.includes("week_fact")
        ) {
          state.weeks.forEach((week: any) => {
            params.showColumns.includes("week_plan")
              ? csv_row.push(item.weeks[week.key].plan)
              : 0;
            params.showColumns.includes("week_plan_manual")
              ? csv_row.push(item.weeks[week.key].plan_manual)
              : 0;
            params.showColumns.includes("week_fact")
              ? csv_row.push(item.weeks[week.key].fact)
              : 0;
          });
        }
        csvContent.push(csv_row);

        if (item.addresses) {
          item.addresses.forEach((address: any) => {
            const csv_row = [
              ...(params.showColumns.includes("category_income") ? [""] : []),
              ...(params.showColumns.includes("client_name")
                ? [address.name]
                : []),
              ...(params.showColumns.includes("manager_name") ? [""] : []),
              ...(params.showColumns.includes("secondary_manager_name")
                ? [""]
                : []),
              ...(params.showColumns.includes("fact_d3")
                ? [address.prev_3_quantity]
                : []),
              ...(state.splitFactPrevMonths &&
              (params.showColumns.includes("fact_d2") ||
                params.showColumns.includes("fact_d1"))
                ? state.prevFactWeekColumns.map(
                    (col: any) =>
                      (address.prevFactByKey &&
                        address.prevFactByKey[col.key]) ||
                      0
                  )
                : []),
              ...(!state.splitFactPrevMonths &&
              params.showColumns.includes("fact_d2")
                ? [address.prev_2_quantity]
                : []),
              ...(!state.splitFactPrevMonths &&
              params.showColumns.includes("fact_d1")
                ? [address.prev_1_quantity]
                : []),
              ...(params.showColumns.includes("fact_avg")
                ? [address.prev_avg_quantity]
                : []),
              ...(params.showColumns.includes("fact_py")
                ? [address.prev_year_quantity]
                : []),
              ...(params.showColumns.includes("plan")
                ? [address.month_plan]
                : []),
              ...(params.showColumns.includes("plan_manual")
                ? [address.month_plan_manual]
                : []),
              ...(params.showColumns.includes("fact")
                ? [address.month_quantity]
                : []),
              ...(params.showColumns.includes("delta") ? [address.delta] : []),
              ...(params.showColumns.includes("percent")
                ? [address.percent]
                : [])
            ];

            if (
              params.showColumns.includes("week_plan") ||
              params.showColumns.includes("week_plan_manual") ||
              params.showColumns.includes("week_fact")
            ) {
              state.weeks.forEach((week: any) => {
                params.showColumns.includes("week_plan")
                  ? csv_row.push(address.weeks[week.key].plan)
                  : 0;
                params.showColumns.includes("week_plan_manual")
                  ? csv_row.push(address.weeks[week.key].plan_manual)
                  : 0;
                params.showColumns.includes("week_fact")
                  ? csv_row.push(address.weeks[week.key].fact)
                  : 0;
              });
            }
            csvContent.push(csv_row);
            if (params.no_products) {
              return;
            }

            const productGroups = address.points
              ? address.points.reduce((acc: any[], point: any) => {
                  const pointRow = [
                    ...(params.showColumns.includes("category_income") ? [""] : []),
                    ...(params.showColumns.includes("client_name")
                      ? [point.name]
                      : []),
                    ...(params.showColumns.includes("manager_name") ? [""] : []),
                    ...(params.showColumns.includes("secondary_manager_name")
                      ? [""]
                      : []),
                    ...(params.showColumns.includes("fact_d3")
                      ? [point.prev_3_quantity]
                      : []),
                    ...(state.splitFactPrevMonths &&
                    (params.showColumns.includes("fact_d2") ||
                      params.showColumns.includes("fact_d1"))
                      ? state.prevFactWeekColumns.map(
                          (col: any) =>
                            (point.prevFactByKey && point.prevFactByKey[col.key]) || 0
                        )
                      : []),
                    ...(!state.splitFactPrevMonths &&
                    params.showColumns.includes("fact_d2")
                      ? [point.prev_2_quantity]
                      : []),
                    ...(!state.splitFactPrevMonths &&
                    params.showColumns.includes("fact_d1")
                      ? [point.prev_1_quantity]
                      : []),
                    ...(params.showColumns.includes("fact_avg")
                      ? [point.prev_avg_quantity]
                      : []),
                    ...(params.showColumns.includes("fact_py")
                      ? [point.prev_year_quantity]
                      : []),
                    ...(params.showColumns.includes("plan") ? [point.month_plan] : []),
                    ...(params.showColumns.includes("plan_manual")
                      ? [point.month_plan_manual]
                      : []),
                    ...(params.showColumns.includes("fact")
                      ? [point.month_quantity]
                      : []),
                    ...(params.showColumns.includes("delta") ? [point.delta] : []),
                    ...(params.showColumns.includes("percent") ? [point.percent] : [])
                  ];
                  if (
                    params.showColumns.includes("week_plan") ||
                    params.showColumns.includes("week_plan_manual") ||
                    params.showColumns.includes("week_fact")
                  ) {
                    state.weeks.forEach((week: any) => {
                      params.showColumns.includes("week_plan")
                        ? pointRow.push((point.weeks[week.key] || {}).plan || 0)
                        : 0;
                      params.showColumns.includes("week_plan_manual")
                        ? pointRow.push((point.weeks[week.key] || {}).plan_manual || 0)
                        : 0;
                      params.showColumns.includes("week_fact")
                        ? pointRow.push((point.weeks[week.key] || {}).fact || 0)
                        : 0;
                    });
                  }
                  acc.push(pointRow);
                  return acc.concat(point.products || []);
                }, [])
              : address.products || [];

            if (productGroups.length) {
              productGroups.forEach((product: any) => {
                if (Array.isArray(product)) {
                  csvContent.push(product);
                  return;
                }
                const csv_row = [
                  ...(params.showColumns.includes("category_income")
                    ? [""]
                    : []),
                  ...(params.showColumns.includes("client_name")
                    ? [product.product_name]
                    : []),
                  ...(params.showColumns.includes("manager_name") ? [""] : []),
                  ...(params.showColumns.includes("secondary_manager_name")
                    ? [""]
                    : []),
                  ...(params.showColumns.includes("fact_d3")
                    ? [product.prev_3_quantity]
                    : []),
                  ...(state.splitFactPrevMonths &&
                  (params.showColumns.includes("fact_d2") ||
                    params.showColumns.includes("fact_d1"))
                    ? state.prevFactWeekColumns.map(
                        (col: any) =>
                          (product.prevFactByKey &&
                            product.prevFactByKey[col.key]) ||
                          0
                      )
                    : []),
                  ...(!state.splitFactPrevMonths &&
                  params.showColumns.includes("fact_d2")
                    ? [product.prev_2_quantity]
                    : []),
                  ...(!state.splitFactPrevMonths &&
                  params.showColumns.includes("fact_d1")
                    ? [product.prev_1_quantity]
                    : []),
                  ...(params.showColumns.includes("fact_avg")
                    ? [product.prev_avg_quantity]
                    : []),
                  ...(params.showColumns.includes("fact_py")
                    ? [product.prev_year_quantity]
                    : []),
                  ...(params.showColumns.includes("plan")
                    ? [product.month_plan]
                    : []),
                  ...(params.showColumns.includes("plan_manual")
                    ? [product.month_plan_manual]
                    : []),
                  ...(params.showColumns.includes("fact")
                    ? [product.month_quantity]
                    : []),
                  ...(params.showColumns.includes("delta")
                    ? [product.delta]
                    : []),
                  ...(params.showColumns.includes("percent")
                    ? [product.percent]
                    : [])
                ];

                if (
                  params.showColumns.includes("week_plan") ||
                  params.showColumns.includes("week_plan_manual") ||
                  params.showColumns.includes("week_fact")
                ) {
                  state.weeks.forEach((week: any) => {
                    params.showColumns.includes("week_plan")
                      ? csv_row.push((product.weeks[week.key] || {}).plan || 0)
                      : 0;
                    params.showColumns.includes("week_plan_manual")
                      ? csv_row.push((product.weeks[week.key] || {}).plan_manual || 0)
                      : 0;
                    params.showColumns.includes("week_fact")
                      ? csv_row.push((product.weeks[week.key] || {}).fact || 0)
                      : 0;
                  });
                }
                csvContent.push(csv_row);
              });
            }
          });
        }
      });

      commit("SET_REPORT_TABLE_PAGE", savedTablePage);
      dispatch("loadData", state.lastQueryParams)
        .then(() => resolve(csvContent))
        .catch(reject);
        })
        .catch(reject);
    });
  },
  toggleAddress({ commit }, { row_id, idx }) {
    commit("TOGGLE_ADDRESS", { row_id, idx });
  },
  togglePoint({ commit }, { row_id, address_idx, point_idx }) {
    commit("TOGGLE_POINT", { row_id, address_idx, point_idx });
  }
};

export const mutations: MutationTree<ShipmentForecastState> = {
  START_LOADING: state => {
    state.loaded = false;
    state.loading = true;
    state.full_reloading = true;
    state.fatalError = false;
  },
  START_RELOADING: state => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: state => {
    state.loaded = true;
    state.loading = false;
    state.full_reloading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  SET_REPORT_DATA(state, data) {
    state.schema = data.schema;
    state.data = data.documents.sort((a: any, b: any) => {
      return a.name.localeCompare(b.name);
    });
    state.settings = data.settings.reduce((acc: any, item: any) => {
      acc[item.name] = item.value;
      return acc;
    }, {});
    state.splitFactPrevMonths = !!data.splitFactPrevMonths;
    if (data.meta && data.meta.total_count != null) {
      state.reportTotalCount = Number(data.meta.total_count);
    } else {
      state.reportTotalCount = data.documents.length;
    }
  },
  SET_REPORT_TABLE_PAGE(state, page: number) {
    state.reportTablePage = page >= 1 ? page : 1;
  },
  SET_LAST_QUERY_PARAMS(state, params: any) {
    state.lastQueryParams = params || {};
  },
  CLEAR(state) {
    state.expandedRows = {};
  },

  TOGGLE_ADDRESS(state, { row_id, idx }) {
    state.items[row_id].addresses[idx].expanded = !state.items[row_id]
      .addresses[idx].expanded;
    state.expandedRows[`${row_id}-${idx}`] =
      state.items[row_id].addresses[idx].expanded;
  },

  TOGGLE_POINT(state, { row_id, address_idx, point_idx }) {
    const point = state.items[row_id].addresses[address_idx].points[point_idx];
    point.expanded = !point.expanded;
    state.expandedRows[`${row_id}-${address_idx}-${point_idx}`] = point.expanded;
  },

  CLEAR_TARGET_UPDATE(state) {
    state.targetUpdate = [];
  },

  RECALC_MONTH_PLAN_VALUE(state, { row_id, address_id, product_id, point_id }) {
    const product = getProductByPath(
      state,
      row_id,
      address_id,
      product_id,
      point_id
    );
    applyMonthPlanDistribution(state, {
      row_id,
      address_id,
      product_id,
      point_id,
      monthPlanKg: product.month_plan
    });
  },

  RECALC_WEEK_PLAN_VALUE(
    state,
    { row_id, address_id, product_id, point_id, week_key }
  ) {
    applyWeekPlanNormalization(state, {
      row_id,
      address_id,
      product_id,
      point_id,
      week_key
    });
  },

  ADD_UPDATE_MONTH_PLAN_VALUE(
    state,
    { row_id, address_id, product_id, point_id, value }
  ) {
    const product = getProductByPath(
      state,
      row_id,
      address_id,
      product_id,
      point_id
    );
    product.month_plan = parseFloat(value) || 0;
    product.delta = product.month_plan - product.month_quantity;
  },
  ADD_UPDATE_PLAN_VALUE(
    state,
    { row_id, address_id, product_id, point_id, week_key, value }
  ) {
    const product = getProductByPath(
      state,
      row_id,
      address_id,
      product_id,
      point_id
    );
    const plan = product.weeks[week_key];
    plan.plan_weight = parseFloat(value) || 0;
  },

  SET_ITEMS(state, term) {
    const weeks: any = {};
    const normWeeks = (a: any) => (Array.isArray(a) ? a : []);
    const prevColMap = new Map<string, any>();
    if (state.splitFactPrevMonths) {
      state.data.forEach((doc: any) => {
        doc.addresses?.forEach((address: any) => {
          address.products?.forEach((product: any) => {
            normWeeks(product.prev_2_weeks_fact).forEach((w: any) => {
              const k = `p2-${w.week_start}`;
              if (!prevColMap.has(k)) {
                prevColMap.set(k, {
                  key: k,
                  sortGroup: 0,
                  week_start: w.week_start,
                  week_end: w.week_end,
                  iso_week: w.iso_week
                });
              }
            });
            normWeeks(product.prev_1_weeks_fact).forEach((w: any) => {
              const k = `p1-${w.week_start}`;
              if (!prevColMap.has(k)) {
                prevColMap.set(k, {
                  key: k,
                  sortGroup: 1,
                  week_start: w.week_start,
                  week_end: w.week_end,
                  iso_week: w.iso_week
                });
              }
            });
          });
        });
      });
      state.prevFactWeekColumns = Array.from(prevColMap.values()).sort(
        (a, b) => {
          if (a.sortGroup !== b.sortGroup) {
            return a.sortGroup - b.sortGroup;
          }
          return String(a.week_start).localeCompare(String(b.week_start));
        }
      );
    } else {
      state.prevFactWeekColumns = [];
    }

    const calc_percent_class = (value: any) => {
      value = parseFloat(value);
      if (value > state.settings.forecast_high) {
        return "is-success";
      } else if (value > state.settings.forecast_low) {
        return "is-warning";
      } else {
        return "is-danger";
      }
    };
    state.items = state.data
      .sort((a: any, b: any) => {
        const key_a = `${a.category_income}-${a.name}`;
        const key_b = `${b.category_income}-${b.name}`;
        return key_a.localeCompare(key_b);
      })
      .reduce((acc: any[], item: any) => {
        if (term && item.name.toLowerCase().includes(term.toLowerCase())) {
          return acc;
        }
        item.weeks = {};
        const client: any = {
          id: item.client_id,
          row_id: acc.length,
          folded: false,
          name: item.name,
          manager_name: item.manager_name,
          secondary_manager_name: item.secondary_manager_name,
          class_name: "client-row",
          month_plan: item.month_plan,
          month_plan_manual: item.month_plan_manual,
          category_income: item.category_income,
          prev_year_quantity: item.prev_year_quantity,
          prev_1_quantity: item.prev_1_quantity,
          prev_2_quantity: item.prev_2_quantity,
          prev_3_quantity: item.prev_3_quantity,
          month_quantity: item.month_quantity,
          prev_avg_quantity:
            (parseFloat(item.prev_1_quantity) +
              parseFloat(item.prev_2_quantity) +
              parseFloat(item.prev_3_quantity)) /
            3,
          addresses: [],
          delta: item.delta,
          percent: item.percent,
          percent_class: calc_percent_class(item.percent),
          weeks: item.weeks
        };
        item.addresses = item.addresses.map((address: any, idx: number) => {
          address.weeks = {};
          const sortedProducts = address.products
            .map((product: any) => {
              if (isArray(product.weeks)) {
                product.weeks = product.weeks.reduce((acc: any, p: any) => {
                  if (p.year) {
                    const useSeg =
                      shipmentForecastUsesSegmentWeekKeys(
                        state.lastQueryParams && state.lastQueryParams.targetDates
                      ) && p.week_start;
                    const key = useSeg
                      ? `${p.year}.${p.week_num}.${p.week_start}`
                      : `${p.year}.${p.week_num}`;
                    p.key = key;
                    if (!weeks[`${key}`]) {
                      weeks[`${key}`] = {
                        key: key,
                        week_start: p.week_start,
                        week_end: p.week_end,
                        week_num: moment(p.week_start).week()
                      };
                    }
                    if (!item.weeks[`${key}`]) {
                      item.weeks[`${key}`] = {
                        plan: 0,
                        plan_manual: 0,
                        coef: 0,
                        fact: 0
                      };
                    }
                    if (!address.weeks[`${key}`]) {
                      address.weeks[`${key}`] = {
                        plan: 0,
                        plan_manual: 0,
                        coef: 0,
                        fact: 0
                      };
                    }
                    const plan = parseFloat(p.plan) || 0;
                    const plan_manual = parseFloat(p.plan_manual) || 0;
                    const plan_weight = parseFloat(p.plan_weight) || 0;
                    const plan_manual_weight =
                      parseFloat(p.plan_manual_weight) || 0;
                    const fact = parseFloat(p.fact) || 0;
                    const delta = parseFloat(p.delta) || 0;
                    const percent = parseFloat(p.percent) || 0;
                    const coef = parseFloat(p.coef) || 0;
                    const avg_quantity = parseFloat(p.avg_quantity) || 0;
                    p.plan = plan;
                    p.plan_manual = plan_manual;
                    p.plan_weight = plan_weight;
                    p.plan_manual_weight = plan_manual_weight;
                    p.fact = fact;
                    p.delta = delta;
                    p.percent = percent;
                    item.weeks[`${key}`].plan += plan_weight;
                    item.weeks[`${key}`].plan_manual += plan_manual_weight;
                    item.weeks[`${key}`].fact += fact;
                    address.weeks[`${key}`].plan += plan_weight;
                    address.weeks[`${key}`].plan_manual += plan_manual_weight;
                    address.weeks[`${key}`].fact += fact;
                    const prev = acc[key];
                    if (!prev) {
                      acc[key] = p;
                      p.plan_ids = isArray(p.plan_ids) ? [...p.plan_ids] : [];
                      p.address_ids = isArray(p.address_ids)
                        ? [...p.address_ids]
                        : [];
                    } else {
                      const prevPlan = parseFloat(prev.plan) || 0;
                      const wCoef = prevPlan + plan;
                      prev.plan = prevPlan + plan;
                      prev.plan_manual =
                        (parseFloat(prev.plan_manual) || 0) + plan_manual;
                      prev.plan_weight =
                        (parseFloat(prev.plan_weight) || 0) + plan_weight;
                      prev.plan_manual_weight =
                        (parseFloat(prev.plan_manual_weight) || 0) +
                        plan_manual_weight;
                      prev.fact = (parseFloat(prev.fact) || 0) + fact;
                      prev.delta = (parseFloat(prev.delta) || 0) + delta;
                      prev.percent =
                        prev.plan > 0
                          ? Math.round(
                              ((parseFloat(prev.fact) || 0) / prev.plan) *
                                10000
                            ) / 100
                          : 0;
                      const prevCoef = parseFloat(prev.coef) || 0;
                      const prevAvg = parseFloat(prev.avg_quantity) || 0;
                      prev.coef =
                        wCoef > 0
                          ? Math.round(
                              ((prevCoef * prevPlan + coef * plan) / wCoef) *
                                10000
                            ) / 10000
                          : prev.coef;
                      prev.avg_quantity =
                        wCoef > 0
                          ? Math.round(
                              ((prevAvg * prevPlan + avg_quantity * plan) /
                                wCoef) *
                                10000
                            ) / 10000
                          : prev.avg_quantity;
                      prev.plan_ids = [
                        ...(isArray(prev.plan_ids) ? prev.plan_ids : []),
                        ...(isArray(p.plan_ids) ? p.plan_ids : [])
                      ];
                      prev.address_ids = [
                        ...(isArray(prev.address_ids) ? prev.address_ids : []),
                        ...(isArray(p.address_ids) ? p.address_ids : [])
                      ];
                    }
                  }
                  return acc;
                }, {});
              }
              product.prev_avg_quantity =
                (parseFloat(product.prev_1_quantity) +
                  parseFloat(product.prev_2_quantity) +
                  parseFloat(product.prev_3_quantity)) /
                3;
              product.product_weight = parseFloat(product.product_weight);
              product.month_plan = parseFloat(product.month_plan);
              product.month_plan_manual = parseFloat(
                product.month_plan_manual
              );
              product.delta = parseFloat(product.delta);
              product.percent = parseFloat(product.percent);
              product.percent_class = calc_percent_class(product.percent);
              const prevFactByKey: Record<string, number> = {};
              if (state.splitFactPrevMonths) {
                normWeeks(product.prev_2_weeks_fact).forEach((w: any) => {
                  prevFactByKey[`p2-${w.week_start}`] =
                    parseFloat(w.fact) || 0;
                });
                normWeeks(product.prev_1_weeks_fact).forEach((w: any) => {
                  prevFactByKey[`p1-${w.week_start}`] =
                    parseFloat(w.fact) || 0;
                });
              }
              product.prevFactByKey = prevFactByKey;
              return product;
            })
            .sort((a: any, b: any) => {
              return a.product_name.localeCompare(b.product_name);
            });
          const addrPrevFact: Record<string, number> = {};
          if (state.splitFactPrevMonths) {
            sortedProducts.forEach((product: any) => {
              Object.keys(product.prevFactByKey || {}).forEach(k => {
                addrPrevFact[k] =
                  (addrPrevFact[k] || 0) +
                  (parseFloat(product.prevFactByKey[k]) || 0);
              });
            });
          }
          client.addresses.push({
            id: address.id,
            child_client_id: address.child_client_id,
            child_client_name: address.child_client_name,
            row_id: acc.length,
            name: address.name,
            folded: false,
            class_name: "address-row",
            expanded: state.expandedRows[`${acc.length}-${idx}`] || false,
            prev_year_quantity: address.prev_year_quantity,
            prev_1_quantity: address.prev_1_quantity,
            prev_2_quantity: address.prev_2_quantity,
            prev_3_quantity: address.prev_3_quantity,
            month_quantity: address.month_quantity,
            month_plan: address.month_plan,
            month_plan_manual: address.month_plan_manual,
            delta: address.delta,
            percent: address.percent,
            percent_class: calc_percent_class(address.percent),
            prev_avg_quantity:
              (parseFloat(address.prev_1_quantity) +
                parseFloat(address.prev_2_quantity) +
                parseFloat(address.prev_3_quantity)) /
              3,
            weeks: address.weeks,
            products: sortedProducts,
            prevFactByKey: addrPrevFact
          });
          return address;
        });

        const groupedChildren = new Map<string, any>();
        const plainAddresses: any[] = [];
        client.addresses.forEach((address: any, idx: number) => {
          if (!address.child_client_id) {
            plainAddresses.push(address);
            return;
          }
          const childKey = String(address.child_client_id);
          if (!groupedChildren.has(childKey)) {
            groupedChildren.set(childKey, {
              id: address.child_client_id,
              row_id: acc.length,
              name: address.child_client_name || address.name,
              folded: false,
              class_name: "address-row",
              expanded: state.expandedRows[`${acc.length}-${idx}`] || false,
              prev_year_quantity: 0,
              prev_1_quantity: 0,
              prev_2_quantity: 0,
              prev_3_quantity: 0,
              month_quantity: 0,
              month_plan: 0,
              month_plan_manual: 0,
              delta: 0,
              percent: 0,
              percent_class: "",
              prev_avg_quantity: 0,
              weeks: {},
              points: [],
              prevFactByKey: {}
            });
          }
          const childNode = groupedChildren.get(childKey);
          const pointIdx = childNode.points.length;
          childNode.points.push({
            ...address,
            class_name: "point-row",
            expanded:
              state.expandedRows[`${acc.length}-${idx}-${pointIdx}`] || false
          });
        });

        groupedChildren.forEach((childNode: any) => {
          childNode.points.forEach((point: any) => {
            childNode.prev_year_quantity += parseFloat(point.prev_year_quantity) || 0;
            childNode.prev_1_quantity += parseFloat(point.prev_1_quantity) || 0;
            childNode.prev_2_quantity += parseFloat(point.prev_2_quantity) || 0;
            childNode.prev_3_quantity += parseFloat(point.prev_3_quantity) || 0;
            childNode.month_quantity += parseFloat(point.month_quantity) || 0;
            childNode.month_plan += parseFloat(point.month_plan) || 0;
            childNode.month_plan_manual += parseFloat(point.month_plan_manual) || 0;
            childNode.delta += parseFloat(point.delta) || 0;
            Object.keys(point.weeks || {}).forEach((weekKey: string) => {
              if (!childNode.weeks[weekKey]) {
                childNode.weeks[weekKey] = { plan: 0, plan_manual: 0, fact: 0 };
              }
              childNode.weeks[weekKey].plan +=
                parseFloat(point.weeks[weekKey].plan) || 0;
              childNode.weeks[weekKey].plan_manual +=
                parseFloat(point.weeks[weekKey].plan_manual) || 0;
              childNode.weeks[weekKey].fact +=
                parseFloat(point.weeks[weekKey].fact) || 0;
            });
            Object.keys(point.prevFactByKey || {}).forEach(k => {
              childNode.prevFactByKey[k] =
                (childNode.prevFactByKey[k] || 0) +
                (parseFloat(point.prevFactByKey[k]) || 0);
            });
          });
          childNode.percent =
            childNode.month_plan > 0
              ? Math.round((childNode.month_quantity / childNode.month_plan) * 10000) /
                100
              : 0;
          childNode.prev_avg_quantity =
            (childNode.prev_1_quantity +
              childNode.prev_2_quantity +
              childNode.prev_3_quantity) /
            3;
          childNode.percent_class = calc_percent_class(childNode.percent);
        });

        client.addresses = plainAddresses.concat(Array.from(groupedChildren.values()));

        const clientPrevFact: Record<string, number> = {};
        if (state.splitFactPrevMonths) {
          client.addresses.forEach((addr: any) => {
            Object.keys(addr.prevFactByKey || {}).forEach(k => {
              clientPrevFact[k] =
                (clientPrevFact[k] || 0) +
                (parseFloat(addr.prevFactByKey[k]) || 0);
            });
          });
        }
        client.prevFactByKey = clientPrevFact;

        acc.push(client);
        return acc;
      }, []);
    if (process.env.NODE_ENV !== "production") {
      const nodeStats = state.items.reduce(
        (acc: any, client: any) => {
          acc.addresses += (client.addresses || []).length;
          (client.addresses || []).forEach((address: any) => {
            acc.points += (address.points || []).length;
            if (address.points) {
              address.points.forEach((point: any) => {
                acc.products += (point.products || []).length;
              });
            } else {
              acc.products += (address.products || []).length;
            }
          });
          return acc;
        },
        { clients: state.items.length, addresses: 0, points: 0, products: 0 }
      );
      // eslint-disable-next-line no-console
      console.debug("[ShipmentForecast] tree nodes", nodeStats);
    }
    if (weeks) {
      state.weeks = Object.values(weeks);
    }
  }
};

const getters = {
  total: (state: any) => {
    const total: any = {
      month_plan: 0,
      month_plan_manual: 0,
      prev_year_quantity: 0,
      prev_1_quantity: 0,
      prev_2_quantity: 0,
      prev_3_quantity: 0,
      month_quantity: 0,
      delta: 0,
      percent: 0,
      weeks: {},
      prevFactByKey: {} as Record<string, number>
    };
    state.items.forEach((i: any) => {
      if (i.class_name === "client-row") {
        total.month_plan += parseFloat(i.month_plan) || 0;
        total.month_plan_manual += parseFloat(i.month_plan_manual) || 0;
        total.delta += parseFloat(i.delta) || 0;

        total.prev_year_quantity += parseFloat(i.prev_year_quantity) || 0;
        total.prev_1_quantity += parseFloat(i.prev_1_quantity) || 0;
        total.prev_2_quantity += parseFloat(i.prev_2_quantity) || 0;
        total.prev_3_quantity += parseFloat(i.prev_3_quantity) || 0;
        total.month_quantity += parseFloat(i.month_quantity) || 0;
        total.percent =
          (total.month_plan > 0 ? total.month_quantity / total.month_plan : 0) *
          100;
        Object.keys(i.weeks).forEach((key: any) => {
          if (!total.weeks[key]) {
            total.weeks[key] = { plan: 0, plan_manual: 0, coef: 0, fact: 0 };
          }
          total.weeks[key].plan += parseFloat(i.weeks[key].plan) || 0;
          total.weeks[key].plan_manual +=
            parseFloat(i.weeks[key].plan_manual) || 0;
          total.weeks[key].fact += parseFloat(i.weeks[key].fact) || 0;
        });
        if (i.prevFactByKey) {
          Object.keys(i.prevFactByKey).forEach((k: string) => {
            total.prevFactByKey[k] =
              (total.prevFactByKey[k] || 0) +
              (parseFloat(i.prevFactByKey[k]) || 0);
          });
        }
      }
    });
    total.prev_avg_quantity =
      (total.prev_1_quantity + total.prev_2_quantity + total.prev_3_quantity) /
      3;
    return total;
  }
};

export const STORE_KEY = "$_shipment_forecast";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations
};
